import { createContext, useContext } from 'react';

import type { CategoryDropdownContextValue } from './types';

export const CategoryDropdownContext = createContext<CategoryDropdownContextValue | null>(null);

/**
 * CategoryDropdown context 조회 — internal. Provider 부재 시 `null` 반환.
 *
 * 호스트는 직접 호출 X — public 호스트용 hook 은 `useCategoryDropdown()` 사용.
 */
export const useCategoryDropdownContext = (): CategoryDropdownContextValue | null =>
  useContext(CategoryDropdownContext);

export interface UseCategoryDropdownResult {
  /** dropdown 의 현재 open 상태 */
  open: boolean;
}

/**
 * `<CategoryDropdown>` 자식 트리에서 dropdown open 상태를 조회.
 *
 * # 사용 예
 *
 * ```tsx
 * function CategoryButton() {
 *   const { open } = useCategoryDropdown();
 *   return <MenuToggleIcon toggled={open} ... />;
 * }
 *
 * <CategoryDropdown>
 *   <Header.Bottom>
 *     <CategoryDropdown.Trigger>
 *       <button>
 *         <CategoryButton />
 *         전체 카테고리
 *       </button>
 *     </CategoryDropdown.Trigger>
 *   </Header.Bottom>
 * </CategoryDropdown>
 * ```
 *
 * # 동작
 *
 * - `<CategoryDropdown>` 자식 트리 안에서 호출 — context 의 open 반환
 * - 트리 밖에서 호출 — dev warn + `{ open: false }` 반환 (SSR / 의외 위치 호출 안전 fallback)
 *
 * `useIsDesktop()` 과 동일 패턴 — Provider 부재 시 안전 default + dev warn.
 */
export function useCategoryDropdown(): UseCategoryDropdownResult {
  const ctx = useContext(CategoryDropdownContext);
  if (process.env.NODE_ENV !== 'production' && ctx === null) {
    // eslint-disable-next-line no-console
    console.warn('[core-ui] useCategoryDropdown() called outside <CategoryDropdown>. Returning `{ open: false }`.');
  }
  return { open: ctx?.open ?? false };
}
